import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { Loading, ToolTip } from '../components/utility/Utility.jsx';
import { IdNameMultiSelectFilter } from './ProductAnalysis';
import { ReactComponent as ImagePlaceholderIcon } from '../assets/images/icons/image-placeholder.svg';
import '../assets/css/components/program.css';
import '../assets/css/views/product-rules.css';

axios.defaults.withCredentials = true;

const API = 'https://api.amplifyloyalty.com';

const WEEKDAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const EMPTY_FORM = {
  name: '',
  enabled: true,
  schedule: {
    frequency: 'daily',
    time_local: '06:00',
    day_of_week: 1,
    day_of_month: 1
  },
  conditions: {
    // Legacy fields kept for coarse pre-filtering and executor safety.
    sales_lookback_days: 90,
    exclude_on_sale: true,
    seed: ''
  },
  conditions_tree: { op: 'and', children: [] },
  selection: null,
  actions: {
    revert_previous: false,
    steps: []
  }
};

const ACTION_TYPE_OPTIONS = [
  { value: '', label: 'Select an action' },
  { value: 'discount', label: 'Apply discount' },
  { value: 'tags', label: 'Update tags' },
  { value: 'status', label: 'Update status' },
  { value: 'collection', label: 'Update collection' },
  { value: 'sales_channel', label: 'Update sales channel' }
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ARCHIVED', label: 'Archived' }
];

function numOrNull(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formToPayload(form) {
  const hasCompareAtExists = treeHasCompareAtExists(form.conditions_tree);
  const prunedTree = buildConditionsTreeForApi(form.conditions_tree);
  return {
    name: form.name.trim(),
    enabled: form.enabled,
    schedule: {
      frequency: form.schedule.frequency,
      time_local: form.schedule.time_local,
      ...(form.schedule.frequency === 'weekly'
        ? { day_of_week: Number(form.schedule.day_of_week) }
        : {}),
      ...(form.schedule.frequency === 'monthly'
        ? { day_of_month: Number(form.schedule.day_of_month) }
        : {})
    },
    conditions: {
      sales_lookback_days: Number(form.conditions.sales_lookback_days) || 90,
      // If the tree explicitly wants compare-at products, don't pre-exclude them.
      exclude_on_sale: hasCompareAtExists ? false : form.conditions.exclude_on_sale !== false,
      seed: numOrNull(form.conditions.seed)
    },
    conditions_tree: prunedTree,
    selection: selectionToPayload(form.selection),
    actions: buildActionsForApi(form.actions)
  };
}

function defaultActionStep(type) {
  if (type === 'discount') {
    return {
      type: 'discount',
      discount_kind: 'percent',
      discount_value: 10,
      discount_pct: 10,
      discount_base: 'price'
    };
  }
  if (type === 'tags') return { type: 'tags', op: 'add', tags: [''] };
  if (type === 'status') return { type: 'status', status: 'ACTIVE' };
  if (type === 'collection') return { type: 'collection', op: 'add', collection_ids: [] };
  if (type === 'sales_channel') return { type: 'sales_channel', op: 'add', publication_ids: [] };
  return { type: '' };
}

function isActionStepComplete(step) {
  if (!step?.type) return false;
  if (step.type === 'discount') {
    const kind = step.discount_kind || 'percent';
    const n = Number(step.discount_value ?? step.discount_pct);
    if (!Number.isFinite(n) || n <= 0) return false;
    if (kind === 'percent') return n > 0 && n < 100;
    return n > 0;
  }
  if (step.type === 'tags') {
    const tags = (step.tags || []).map((s) => String(s).trim()).filter(Boolean);
    return tags.length > 0 && (step.op === 'add' || step.op === 'remove');
  }
  if (step.type === 'status') {
    return ['ACTIVE', 'DRAFT', 'ARCHIVED'].includes(String(step.status || '').toUpperCase());
  }
  if (step.type === 'collection') {
    const ids = (step.collection_ids || [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    return ids.length === 1 && (step.op === 'add' || step.op === 'remove');
  }
  if (step.type === 'sales_channel') {
    const ids = (step.publication_ids || [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    return ids.length === 1 && (step.op === 'add' || step.op === 'remove');
  }
  return false;
}

function buildActionsForApi(actions) {
  const a = actions || {};
  const steps = (Array.isArray(a.steps) ? a.steps : [])
    .filter(isActionStepComplete)
    .map((step) => {
      if (step.type === 'discount') {
        const kind = step.discount_kind === 'fixed' ? 'fixed' : 'percent';
        const value = Number(step.discount_value ?? step.discount_pct);
        const base =
          step.discount_base === 'compare_at_price' ? 'compare_at_price' : 'price';
        const out = {
          type: 'discount',
          discount_kind: kind,
          discount_value: kind === 'percent' ? Math.floor(value) : value,
          discount_base: base
        };
        if (kind === 'percent') out.discount_pct = out.discount_value;
        return out;
      }
      if (step.type === 'tags') {
        return {
          type: 'tags',
          op: step.op === 'remove' ? 'remove' : 'add',
          tags: (step.tags || []).map((s) => String(s).trim()).filter(Boolean)
        };
      }
      if (step.type === 'status') {
        return { type: 'status', status: String(step.status).toUpperCase() };
      }
      if (step.type === 'collection') {
        const collection_ids = (step.collection_ids || [])
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n > 0)
          .slice(0, 1);
        return {
          type: 'collection',
          op: step.op === 'remove' ? 'remove' : 'add',
          collection_ids
        };
      }
      if (step.type === 'sales_channel') {
        const publication_ids = (step.publication_ids || [])
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n > 0)
          .slice(0, 1);
        return {
          type: 'sales_channel',
          op: step.op === 'remove' ? 'remove' : 'add',
          publication_ids
        };
      }
      return null;
    })
    .filter(Boolean);
  return {
    revert_previous: Boolean(a.revert_previous),
    steps
  };
}

function legacyActionsToFormSteps(actions) {
  const a = actions || {};
  const steps = [];
  const discount = a.pricing?.discount_pct;
  if (discount != null && discount !== '' && Number(discount) > 0) {
    const pct = Math.floor(Number(discount));
    steps.push({
      _uiId: `a-${Date.now()}-d`,
      type: 'discount',
      discount_kind: 'percent',
      discount_value: pct,
      discount_pct: pct,
      discount_base: 'price'
    });
  }
  const rotationTag = a.tags?.tag ? String(a.tags.tag).trim() : '';
  const addTags = [
    ...(rotationTag ? [rotationTag] : []),
    ...(a.tags?.add || []).map((s) => String(s).trim()).filter(Boolean)
  ];
  if (addTags.length) {
    steps.push({ _uiId: `a-${Date.now()}-ta`, type: 'tags', op: 'add', tags: addTags });
  }
  const removeTags = (a.tags?.remove || []).map((s) => String(s).trim()).filter(Boolean);
  if (removeTags.length) {
    steps.push({ _uiId: `a-${Date.now()}-tr`, type: 'tags', op: 'remove', tags: removeTags });
  }
  return steps;
}

function actionsToForm(actions) {
  const a = actions || {};
  if (Array.isArray(a.steps)) {
    return {
      revert_previous: Boolean(a.revert_previous),
      steps: a.steps.map((s, i) => ({
        _uiId: s._uiId || `a-${Date.now()}-${i}`,
        ...s,
        tags: Array.isArray(s.tags) ? s.tags : s.type === 'tags' ? [''] : undefined
      }))
    };
  }
  return {
    revert_previous: Boolean(a.tags?.replace_existing),
    steps: legacyActionsToFormSteps(a)
  };
}

/** Mirrors backend leaf validation — preview/save only when every filled row is complete. */
function isConditionLeafComplete(leaf) {
  if (!leaf?.field || !leaf?.op) return false;
  const { field, op } = leaf;
  const value = leaf.value || {};

  if (field === 'collection') {
    const ids = (value.collection_ids || [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    return ids.length === 1;
  }
  if (field === 'product') {
    const ids = (value.product_ids || [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    return ids.length > 0;
  }
  if (field === 'product_type') {
    const types = (value.product_types || []).map((s) => String(s).trim()).filter(Boolean);
    return types.length > 0;
  }
  if (field === 'status') {
    const status = String(value?.status || '').toUpperCase();
    return ['ACTIVE', 'DRAFT', 'ARCHIVED'].includes(status);
  }
  if (field === 'created_at') {
    if (op === 'in_last' || op === 'more_than') {
      const days = Number(value.days);
      return Number.isFinite(days) && days > 0;
    }
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value.date || '').trim());
  }
  if (field === 'units_sold') {
    const lookback = Number(value.lookback_days);
    if (!Number.isFinite(lookback) || lookback <= 0) return false;
    const amount = Number(value.amount);
    if (!Number.isFinite(amount)) return false;
    if (op === 'in_top' || op === 'in_bottom') {
      if (value.mode !== 'percent' && value.mode !== 'units') return false;
      if (amount <= 0) return false;
      if (value.mode === 'percent' && amount > 100) return false;
      return true;
    }
    return amount >= 0;
  }
  if (field === 'inventory') {
    const amount = Number(value.amount);
    return Number.isFinite(amount) && amount >= 0;
  }
  if (field === 'sales_channel') {
    const publicationId = Number(value.publication_id);
    return Number.isFinite(publicationId) && publicationId > 0;
  }
  if (field === 'compare_at_price') return op === 'exists' || op === 'is_null';
  if (field === 'on_sale') return true;
  return false;
}

function getConditionLeavesFromTree(tree) {
  if (!tree || tree.op !== 'and' || !Array.isArray(tree.children)) return [];
  return tree.children.flatMap((g) =>
    g && g.op === 'or' && Array.isArray(g.children) ? g.children : []
  );
}

function ruleUsesSalesChannelActions(rule) {
  const steps = rule?.actions?.steps;
  return Array.isArray(steps) && steps.some((s) => s?.type === 'sales_channel');
}

/** API tree with only complete leaves — skips empty placeholders and in-progress rows. */
function buildConditionsTreeForApi(tree) {
  if (!tree || tree.op !== 'and' || !Array.isArray(tree.children)) {
    return { op: 'and', children: [] };
  }
  const groups = tree.children
    .map((g) => {
      if (!g || g.op !== 'or' || !Array.isArray(g.children)) return null;
      const children = g.children
        .filter(isConditionLeafComplete)
        .map((leaf) => {
          const out = { field: leaf.field, op: leaf.op };
          if (leaf.value != null) out.value = leaf.value;
          return out;
        });
      if (!children.length) return null;
      return { op: 'or', children };
    })
    .filter(Boolean);
  return { op: 'and', children: groups };
}

function hasIncompleteStartedLeaf(tree) {
  return getConditionLeavesFromTree(tree).some(
    (leaf) => leaf?.field && !isConditionLeafComplete(leaf)
  );
}

function treeHasCompareAtExists(tree) {
  return getConditionLeavesFromTree(tree).some(
    (n) => n?.field === 'compare_at_price' && n?.op === 'exists'
  );
}

function selectionToPayload(selection) {
  if (selection == null) return null;
  const count = Number(selection.count);
  if (!Number.isFinite(count) || count <= 0) return null;
  return {
    count: Math.max(1, Math.min(500, Math.floor(count))),
    method: 'random'
  };
}

function formToPreviewPayload(form) {
  const hasCompareAtExists = treeHasCompareAtExists(form.conditions_tree);

  return {
    conditions: {
      sales_lookback_days: Number(form.conditions.sales_lookback_days) || 90,
      exclude_on_sale: hasCompareAtExists ? false : form.conditions.exclude_on_sale !== false,
      seed: numOrNull(form.conditions.seed)
    },
    conditions_tree: buildConditionsTreeForApi(form.conditions_tree),
    selection: selectionToPayload(form.selection)
  };
}

function ruleToForm(rule) {
  const c = rule.conditions || {};
  const a = rule.actions || {};
  return {
    name: rule.name || '',
    enabled: rule.enabled !== false,
    schedule: {
      frequency: rule.schedule?.frequency || 'daily',
      time_local: rule.schedule?.time_local || '06:00',
      day_of_week: rule.schedule?.day_of_week ?? 1,
      day_of_month: rule.schedule?.day_of_month ?? 1
    },
    conditions: {
      sales_lookback_days: c.sales_lookback_days ?? 90,
      exclude_on_sale: c.exclude_on_sale !== false,
      seed: c.seed ?? ''
    },
    conditions_tree: rule.conditions_tree || { op: 'and', children: [] },
    selection:
      rule.selection != null && Number.isFinite(Number(rule.selection.count))
        ? { count: rule.selection.count, method: 'random' }
        : null,
    actions: actionsToForm(rule.actions)
  };
}

const DEFAULT_STORE_TIMEZONE = 'America/New_York';

function formatStoreDateTime(value, timezone) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const tz = timezone || DEFAULT_STORE_TIMEZONE;
  try {
    return d.toLocaleString(undefined, {
      timeZone: tz,
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch {
    return d.toLocaleString();
  }
}

function formatLastRun(rule, timezone) {
  if (!rule.last_run_at) return 'Never';
  const status = rule.last_run_status ? ` · ${rule.last_run_status}` : '';
  return `${formatStoreDateTime(rule.last_run_at, timezone)}${status}`;
}

function ruleRunBlocked(rule, runBusyId) {
  return runBusyId === rule.id || Boolean(rule.run_in_progress);
}

function runNowButtonLabel(rule, runBusyId) {
  if (runBusyId === rule.id) return 'Running…';
  if (rule.run_in_progress) return 'Run in progress…';
  return 'Run now';
}

export default function ProductRules() {
  const { getJwtToken, canWrite } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [timezone, setTimezone] = useState('');
  const [writeScope, setWriteScope] = useState(true);
  const [publicationsScope, setPublicationsScope] = useState(true);
  const [err, setErr] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [runBusyId, setRunBusyId] = useState(null);
  const [toggleBusyId, setToggleBusyId] = useState(null);
  const [historyRuleId, setHistoryRuleId] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);
  const [facets, setFacets] = useState({ collections: [], product_types: [], sales_channels: [], locations: [] });
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogProductsLoading, setCatalogProductsLoading] = useState(false);
  const productMetaCacheRef = useRef(new Map());
  const [eligibilityCount, setEligibilityCount] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewProducts, setPreviewProducts] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewPage, setPreviewPage] = useState(0);
  const [previewLimit] = useState(20);
  const [previewTotal, setPreviewTotal] = useState(0);

  const req = useCallback(
    async (config) => {
      const token = await getJwtToken();
      return axios({
        ...config,
        headers: { ...config.headers, Authorization: `Bearer ${token}` }
      });
    },
    [getJwtToken]
  );

  const loadRules = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setErr('');
    try {
      const res = await req({ method: 'GET', url: `${API}/app/product-analysis/rules` });
      setRules(res.data?.rules || []);
      setTimezone(res.data?.timezone || '');
      setWriteScope(res.data?.shopify_write_products !== false);
      setPublicationsScope(res.data?.shopify_write_publications !== false);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Failed to load rules.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [req]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  // Fetch all catalog metadata when the editor opens
  useEffect(() => {
    if (!editorOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await req({
          method: 'GET',
          url: `${API}/app/product-analysis/rules/catalog-metadata?include=collections,locations,product_types,sales_channels`
        });
        if (cancelled) return;
        const data = res.data || {};
        setFacets({
          collections: (data.collections || []).map((c) => ({ id: c.id, name: c.name })),
          locations: data.locations || [],
          product_types: data.product_types || [],
          sales_channels: data.sales_channels || []
        });
      } catch (e) {
        if (!cancelled) console.error('Failed to fetch catalog metadata:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [req, editorOpen]);

  // Full catalog product list for the Product eligibility filter (multi-select).
  useEffect(() => {
    if (!editorOpen) return;
    let cancelled = false;
    setCatalogProductsLoading(true);
    (async () => {
      try {
        const res = await req({
          method: 'POST',
          url: `${API}/app/product-analysis/filter-candidates`,
          data: { titles_scope: 'all', titles_only: true }
        });
        if (cancelled) return;
        const incomingTitles = Array.isArray(res.data?.titles) ? res.data.titles : [];
        const products = incomingTitles
          .map((t) => ({
            id: Number(t?.id),
            name: String(t?.title ?? t?.name ?? ''),
            image_url: t?.image_url ? String(t.image_url) : null
          }))
          .filter((p) => Number.isFinite(p.id) && p.id > 0 && p.name);
        setCatalogProducts(products);
        for (const p of products) {
          productMetaCacheRef.current.set(String(p.id), {
            name: p.name,
            image_url: p.image_url
          });
        }
      } catch (e) {
        if (!cancelled) console.error('Failed to fetch catalog products:', e);
      } finally {
        if (!cancelled) setCatalogProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [req, editorOpen]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setEditorOpen(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setForm(ruleToForm(rule));
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const saveRule = async () => {
    if (!canWrite) return;
    setSaving(true);
    setErr('');
    try {
      const payload = formToPayload(form);
      if (editingId) {
        await req({
          method: 'PATCH',
          url: `${API}/app/product-analysis/rules/${editingId}`,
          data: payload
        });
      } else {
        await req({
          method: 'POST',
          url: `${API}/app/product-analysis/rules`,
          data: payload
        });
      }
      closeEditor();
      await loadRules({ silent: true });
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (rule) => {
    if (!canWrite || toggleBusyId === rule.id) return;
    const nextEnabled = !rule.enabled;
    const prevEnabled = rule.enabled;
    setToggleBusyId(rule.id);
    setRules((prev) =>
      prev.map((r) => (r.id === rule.id ? { ...r, enabled: nextEnabled } : r))
    );
    try {
      const res = await req({
        method: 'PATCH',
        url: `${API}/app/product-analysis/rules/${rule.id}`,
        data: { enabled: nextEnabled }
      });
      const updated = res.data?.rule;
      if (updated?.id) {
        setRules((prev) =>
          prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))
        );
      }
    } catch (e) {
      setRules((prev) =>
        prev.map((r) => (r.id === rule.id ? { ...r, enabled: prevEnabled } : r))
      );
      setErr(e.response?.data?.message || e.message || 'Update failed.');
    } finally {
      setToggleBusyId(null);
    }
  };

  const deleteRule = async (rule) => {
    if (!canWrite) return;
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await req({
        method: 'DELETE',
        url: `${API}/app/product-analysis/rules/${rule.id}`
      });
      if (historyRuleId === rule.id) setHistoryRuleId(null);
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Delete failed.');
      await loadRules({ silent: true });
    }
  };

  const syncRuleRunInProgress = useCallback((ruleId, list) => {
    const active = (list || []).some((r) => r.status === 'running');
    setRules((prev) =>
      prev.map((r) => (r.id === ruleId ? { ...r, run_in_progress: active } : r))
    );
  }, []);

  const runNow = async (rule) => {
    if (!canWrite) {
      setErr('You do not have write permissions.');
      return;
    }
    if (rule.run_in_progress) return;
    setRunBusyId(rule.id);
    setHistoryRuleId(rule.id);
    setRules((prev) =>
      prev.map((r) => (r.id === rule.id ? { ...r, run_in_progress: true } : r))
    );
    setErr('');
    try {
      const baseline = await loadRuns(rule.id, { silent: true });
      const baselineIds = new Set((baseline || []).map((r) => r.id));

      await req({
        method: 'POST',
        url: `${API}/app/product-analysis/rules/${rule.id}/run`
      });

      await loadRules({ silent: true });
      await pollRunsUntilFinished(rule.id, baselineIds);
    } catch (e) {
      setRules((prev) =>
        prev.map((r) => (r.id === rule.id ? { ...r, run_in_progress: false } : r))
      );
      setErr(e.response?.data?.message || e.message || 'Run failed.');
    } finally {
      setRunBusyId(null);
    }
  };

  const loadRuns = useCallback(
    async (ruleId, { silent = false } = {}) => {
      if (!silent) setRunsLoading(true);
      try {
        const res = await req({
          method: 'GET',
          url: `${API}/app/product-analysis/rules/${ruleId}/runs`
        });
        const list = res.data?.runs || [];
        setRuns(list);
        syncRuleRunInProgress(ruleId, list);
        return list;
      } catch (e) {
        setErr(e.response?.data?.message || e.message || 'Failed to load run history.');
        return [];
      } finally {
        if (!silent) setRunsLoading(false);
      }
    },
    [req, syncRuleRunInProgress]
  );

  const pollRunsUntilFinished = useCallback(
    async (ruleId, baselineIds) => {
      const known = baselineIds instanceof Set ? baselineIds : new Set(baselineIds || []);
      const maxAttempts = 45;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 600 : 2000));
        const current = await loadRuns(ruleId, { silent: true });
        const newRun = (current || []).find((r) => !known.has(r.id));
        if (newRun && newRun.status !== 'running') return newRun;
      }
      await loadRuns(ruleId, { silent: true });
      return null;
    },
    [loadRuns]
  );

  useEffect(() => {
    if (historyRuleId) loadRuns(historyRuleId);
    else {
      setRuns([]);
      setExpandedRunId(null);
      setRunDetail(null);
    }
  }, [historyRuleId, loadRuns]);

  const loadRunDetail = useCallback(
    async (ruleId, runId) => {
      if (expandedRunId === runId) {
        setExpandedRunId(null);
        setRunDetail(null);
        return;
      }
      setExpandedRunId(runId);
      setRunDetailLoading(true);
      setRunDetail(null);
      try {
        const res = await req({
          method: 'GET',
          url: `${API}/app/product-analysis/rules/${ruleId}/runs/${runId}`
        });
        setRunDetail(res.data?.run || null);
      } catch (e) {
        setErr(e.response?.data?.message || e.message || 'Failed to load run detail.');
        setExpandedRunId(null);
      } finally {
        setRunDetailLoading(false);
      }
    },
    [req, expandedRunId]
  );

  const updateForm = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i += 1) {
        cur[keys[i]] = { ...cur[keys[i]] };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const newUiId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  // Builder model: root AND groups; each group is an OR list of leaf conditions.
  // This mirrors the AI Analysis tag search UI (OR terms inside a group, AND between groups).
  const normalizeBuilderTree = useCallback(
    (raw) => {
      const emptyLeaf = () => ({ _uiId: newUiId(), field: '', op: '', value: {} });
      const emptyOrGroup = () => ({ _uiId: newUiId(), op: 'or', children: [emptyLeaf()] });

      if (!raw || typeof raw !== 'object' || raw.op !== 'and') {
        return { _uiId: newUiId(), op: 'and', children: [emptyOrGroup()] };
      }
      const andChildren = Array.isArray(raw.children) ? raw.children : [];
      const groups = andChildren
        .filter(Boolean)
        .map((g) => {
          if (!g || typeof g !== 'object' || g.op !== 'or') return emptyOrGroup();
          const kids = Array.isArray(g.children) ? g.children : [];
          const leaves = kids
            .filter(Boolean)
            .map((l) => ({
              _uiId: l._uiId || newUiId(),
              field: l.field || '',
              op: l.op || '',
              value: l.value || {}
            }));
          return {
            _uiId: g._uiId || newUiId(),
            op: 'or',
            children: leaves.length ? leaves : [emptyLeaf()]
          };
        });
      return { _uiId: raw._uiId || newUiId(), op: 'and', children: groups.length ? groups : [emptyOrGroup()] };
    },
    []
  );

  useEffect(() => {
    if (!editorOpen) return;
    setForm((prev) => ({
      ...prev,
      conditions_tree: normalizeBuilderTree(prev.conditions_tree)
    }));
  }, [editorOpen, normalizeBuilderTree]);

  const updateTree = (fn) => {
    setForm((prev) => ({
      ...prev,
      conditions_tree: normalizeBuilderTree(fn(prev.conditions_tree))
    }));
  };

  // Flat join model:
  // - `items[i]` is a leaf condition
  // - `joins[i]` is the join between items[i] and items[i+1] ('and'|'or')
  const getJoinModel = () => {
    const tree = form.conditions_tree || { op: 'and', children: [] };
    const groups = Array.isArray(tree.children) ? tree.children : [];
    const items = [];
    const joins = [];
    for (let gi = 0; gi < groups.length; gi += 1) {
      const g = groups[gi];
      const leaves = Array.isArray(g?.children) ? g.children : [];
      for (let li = 0; li < leaves.length; li += 1) {
        items.push(leaves[li]);
        const isLastLeafInGroup = li === leaves.length - 1;
        const isLastGroup = gi === groups.length - 1;
        if (!isLastLeafInGroup) joins.push('or');
        else if (!isLastGroup) joins.push('and');
      }
    }
    return { items, joins };
  };

  const setJoinModel = (model) => {
    const items = Array.isArray(model.items) ? model.items : [];
    const joins = Array.isArray(model.joins) ? model.joins : [];
    const groups = [];
    let cur = { op: 'or', children: [] };
    for (let i = 0; i < items.length; i += 1) {
      cur.children.push(items[i]);
      const join = joins[i];
      if (join === 'and') {
        groups.push(cur);
        cur = { op: 'or', children: [] };
      }
    }
    if (cur.children.length) groups.push(cur);
    updateTree(() => ({ op: 'and', children: groups }));
  };

  const updateJoin = (joinIndex, value) => {
    const { items, joins } = getJoinModel();
    const next = [...joins];
    next[joinIndex] = value === 'or' ? 'or' : 'and';
    setJoinModel({ items, joins: next });
  };

  const removeItem = (itemIndex) => {
    const { items, joins } = getJoinModel();
    if (items.length <= 1) {
      setJoinModel({ items: [{ _uiId: newUiId(), field: '', op: '', value: {} }], joins: [] });
      return;
    }
    const nextItems = items.filter((_, idx) => idx !== itemIndex);
    const nextJoins = joins.filter((_, idx) => idx !== itemIndex && idx !== itemIndex - 1);
    // If we removed a middle item, we need to insert a join between neighbors.
    if (itemIndex > 0 && itemIndex < items.length - 1) {
      const insertAt = itemIndex - 1;
      nextJoins.splice(insertAt, 0, joins[itemIndex - 1] || 'and');
    }
    setJoinModel({ items: nextItems, joins: nextJoins });
  };

  const updateLeaf = (leafUiId, patch) => {
    const { items, joins } = getJoinModel();
    const nextItems = items.map((c) => (c._uiId === leafUiId ? { ...c, ...patch } : c));
    setJoinModel({ items: nextItems, joins });
  };

  const FIELD_OPTIONS = [
    { value: '', label: 'Select a condition' },
    { value: 'collection', label: 'Collection' },
    { value: 'product', label: 'Product' },
    { value: 'product_type', label: 'Product type' },
    { value: 'status', label: 'Status' },
    { value: 'created_at', label: 'Created at' },
    { value: 'units_sold', label: 'Units sold' },
    { value: 'inventory', label: 'Available inventory' },
    { value: 'sales_channel', label: 'Sales channel' },
    { value: 'compare_at_price', label: 'Compare-at price' }
  ];

  const OPERATORS = {
    collection: [
      { value: 'includes', label: 'includes' },
      { value: 'excludes', label: 'does not include' }
    ],
    product: [
      { value: 'is', label: 'is' },
      { value: 'is_not', label: 'is not' }
    ],
    product_type: [
      { value: 'is', label: 'is' },
      { value: 'is_not', label: 'is not' }
    ],
    status: [
      { value: 'is', label: 'is' },
      { value: 'is_not', label: 'is not' }
    ],
    created_at: [
      { value: 'before', label: 'before' },
      { value: 'after', label: 'after' },
      { value: 'in_last', label: 'in the last' },
      { value: 'more_than', label: 'more than' }
    ],
    units_sold: [
      { value: 'lt', label: 'less than' },
      { value: 'gt', label: 'greater than' },
      { value: 'eq', label: 'equal to' },
      { value: 'in_top', label: 'in the top' },
      { value: 'in_bottom', label: 'in the bottom' }
    ],
    inventory: [
      { value: 'lt', label: 'less than' },
      { value: 'lte', label: 'less than or equal to' },
      { value: 'gt', label: 'greater than' },
      { value: 'gte', label: 'greater than or equal to' },
      { value: 'eq', label: 'equal to' }
    ],
    sales_channel: [
      { value: 'includes', label: 'includes' },
      { value: 'excludes', label: 'excludes' }
    ],
    compare_at_price: [
      { value: 'exists', label: 'exists' },
      { value: 'is_null', label: 'is null' }
    ]
  };

  const renderLeafValueParts = (leaf) => {
    const field = leaf.field;
    const op = leaf.op;
    const value = leaf.value || {};

    if (!field) return [];

    if (field === 'collection') {
      const selectedId = (value.collection_ids || [])[0];
      return [
        <select
          key={`${leaf._uiId}-collection`}
          className="product-rules__input"
          style={{ minWidth: 240 }}
          value={selectedId ? String(selectedId) : ''}
          onChange={(e) => {
            const id = Number(e.target.value);
            updateLeaf(leaf._uiId, {
              value: {
                collection_ids: Number.isFinite(id) && id > 0 ? [id] : []
              }
            });
          }}
        >
          <option value="">Select collection</option>
          {facets.collections.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || `Collection ${c.id}`}
            </option>
          ))}
        </select>
      ];
    }

    if (field === 'product') {
      return [
        <div key={`${leaf._uiId}-product`} className="product-rules__product-multiselect">
          <IdNameMultiSelectFilter
            label="Product"
            options={catalogProducts}
            value={(value.product_ids || []).map(String)}
            onChange={(ids) => {
              const product_ids = ids
                .map((id) => Number(id))
                .filter((n) => Number.isFinite(n) && n > 0);
              for (const id of ids) {
                const p = catalogProducts.find((x) => String(x.id) === String(id));
                if (p) {
                  productMetaCacheRef.current.set(String(id), {
                    name: p.name,
                    image_url: p.image_url || null
                  });
                }
              }
              updateLeaf(leaf._uiId, { value: { product_ids } });
            }}
            showSearch
            emptyText={catalogProductsLoading ? 'Loading products…' : 'No products in catalog'}
          />
        </div>
      ];
    }

    if (field === 'product_type') {
      return [
        <select
          key={`${leaf._uiId}-product-type`}
          className="product-rules__input"
          style={{ minWidth: 240 }}
          value={(value.product_types || [])[0] || ''}
          onChange={(e) =>
            updateLeaf(leaf._uiId, {
              value: {
                product_types: e.target.value ? [e.target.value] : []
              }
            })
          }
        >
          <option value="">Select product type</option>
          {(facets.product_types || []).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      ];
    }

    if (field === 'created_at') {
      if (op === 'in_last' || op === 'more_than') {
        return [
          <div key={`${leaf._uiId}-days`} className="product-rules__value-part">
            <input
              type="number"
              min={1}
              className="product-rules__input"
              style={{ width: 120 }}
              value={value.days ?? ''}
              onChange={(e) =>
                updateLeaf(leaf._uiId, { value: { days: Number(e.target.value || 0) } })
              }
            />
            <span className="product-rules__hint">
              days{op === 'more_than' ? ' ago' : ''}
            </span>
          </div>
        ];
      }
      return [
        <input
          key={`${leaf._uiId}-date`}
          type="date"
          className="product-rules__input"
          value={value.date ?? ''}
          onChange={(e) => updateLeaf(leaf._uiId, { value: { date: e.target.value } })}
        />
      ];
    }

    if (field === 'units_sold') {
      const isRank = op === 'in_top' || op === 'in_bottom';
      const parts = [
        <div key={`${leaf._uiId}-amount`} className="product-rules__value-part">
          <input
            type="number"
            min={0}
            className="product-rules__input"
            style={{ width: 120 }}
            value={value.amount ?? ''}
            onChange={(e) =>
              updateLeaf(leaf._uiId, {
                value: { ...value, amount: Number(e.target.value || 0) }
              })
            }
          />
        </div>
      ];
      if (isRank) {
        parts.push(
          <div key={`${leaf._uiId}-mode-in-last`} className="product-rules__value-part">
            <select
              className="product-rules__input"
              style={{ width: 140 }}
              value={value.mode || 'percent'}
              onChange={(e) =>
                updateLeaf(leaf._uiId, { value: { ...value, mode: e.target.value } })
              }
            >
              <option value="percent">%</option>
              <option value="units">products</option>
            </select>
            <span className="product-rules__hint">in the last</span>
          </div>
        );
      } else {
        parts.push(
          <span key={`${leaf._uiId}-in-last`} className="product-rules__hint product-rules__value-label">
            in the last
          </span>
        );
      }
      parts.push(
        <div key={`${leaf._uiId}-lookback`} className="product-rules__value-part">
          <input
            type="number"
            min={1}
            className="product-rules__input"
            style={{ width: 100 }}
            value={value.lookback_days ?? 90}
            onChange={(e) =>
              updateLeaf(leaf._uiId, {
                value: { ...value, lookback_days: Number(e.target.value || 0) }
              })
            }
          />
          <span className="product-rules__hint">days</span>
        </div>
      );
      return parts;
    }

    if (field === 'inventory') {
      const selectedLocationId = value.location_id ? String(value.location_id) : '';
      return [
        <input
          key={`${leaf._uiId}-amount`}
          type="number"
          min={0}
          className="product-rules__input"
          style={{ width: 160 }}
          value={value.amount ?? ''}
          onChange={(e) =>
            updateLeaf(leaf._uiId, {
              value: { ...value, amount: Number(e.target.value || 0) }
            })
          }
        />,
        <span key={`${leaf._uiId}-at-label`} className="product-rules__hint" style={{ alignSelf: 'center' }}>
          at
        </span>,
        <select
          key={`${leaf._uiId}-location`}
          className="product-rules__input"
          style={{ minWidth: 200 }}
          value={selectedLocationId}
          onChange={(e) => {
            const id = e.target.value ? Number(e.target.value) : null;
            updateLeaf(leaf._uiId, {
              value: { ...value, location_id: id }
            });
          }}
        >
          <option value="">all locations</option>
          {(facets.locations || []).map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name}
            </option>
          ))}
        </select>
      ];
    }

    if (field === 'sales_channel') {
      const selectedPublicationId = value.publication_id || '';
      return [
        <select
          key={`${leaf._uiId}-channels`}
          className="product-rules__input"
          style={{ minWidth: 240 }}
          value={selectedPublicationId}
          onChange={(e) => {
            const publicationId = e.target.value ? Number(e.target.value) : null;
            updateLeaf(leaf._uiId, {
              value: { publication_id: publicationId }
            });
          }}
        >
          <option value="">Select sales channel</option>
          {(facets.sales_channels || []).map((sc) => (
            <option key={sc.shopify_publication_id} value={sc.shopify_publication_id}>
              {sc.name}
            </option>
          ))}
        </select>
      ];
    }

    if (field === 'status') {
      return [
        <select
          key={`${leaf._uiId}-status`}
          className="product-rules__input"
          style={{ width: 160 }}
          value={value.status || 'ACTIVE'}
          onChange={(e) =>
            updateLeaf(leaf._uiId, {
              value: { status: e.target.value }
            })
          }
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ];
    }

    return [];
  };

  const renderSelectedProductRows = (leaf) => {
    const ids = (leaf.value?.product_ids || [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!ids.length) return null;

    return (
      <ul className="product-rules__selected-products-list">
        {ids.map((id) => {
          const fromCatalog = catalogProducts.find((p) => Number(p.id) === id);
          const meta = productMetaCacheRef.current.get(String(id)) || {};
          const name = meta.name || fromCatalog?.name || `Product ${id}`;
          const imageUrl = meta.image_url || fromCatalog?.image_url || null;
          return (
            <li key={id} className="product-rules__selected-product-row">
              <div className="product-rules__selected-product-content">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt=""
                    className="product-rules__selected-product-img"
                  />
                ) : (
                  <div className="product-rules__selected-product-img product-rules__selected-product-img--placeholder">
                    <ImagePlaceholderIcon className="product-rules__preview-placeholder-icon" />
                  </div>
                )}
                <span className="product-rules__selected-product-title">{name}</span>
              </div>
              <button
                type="button"
                className="product-rules__selected-product-remove"
                onClick={() => {
                  const filtered = (leaf.value?.product_ids || []).filter(
                    (pid) => Number(pid) !== id
                  );
                  updateLeaf(leaf._uiId, { value: { product_ids: filtered } });
                }}
                aria-label={`Remove ${name}`}
              >
                ×
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderAndOrBuilder = () => {
    const { items, joins } = getJoinModel();

    // Compute bordered OR-groups by splitting on AND joins.
    const groups = [];
    let cur = [];
    for (let i = 0; i < items.length; i += 1) {
      cur.push({ item: items[i], index: i });
      if (joins[i] === 'and') {
        groups.push(cur);
        cur = [];
      }
    }
    if (cur.length) groups.push(cur);

    return (
      <div>
        {groups.map((g, gi) => {
          const isLastGroup = gi === groups.length - 1;
          const lastIndexInGroup = g[g.length - 1]?.index ?? -1;

          return (
            <div key={`gwrap-${gi}`}>
              <div className="product-rules__condition-group" style={{ marginBottom: 12 }}>
                {g.map(({ item, index }) => {
                  const isLastItemOverall = index >= items.length - 1;
                  const joinAfter = joins[index] || null;
                  const showOrJoinInsideGroup = joinAfter === 'or' && !isLastItemOverall;

              const fieldOps = OPERATORS[item.field] || [];
              const hasField = Boolean(item.field);
              const opValue = hasField ? item.op || fieldOps[0]?.value || '' : '';
              const isProductField = item.field === 'product';
              return (
                <div key={item._uiId} className={isProductField ? 'product-rules__condition-block--product' : undefined}>
                  <div
                    className={
                      isProductField
                        ? 'product-rules__condition-row product-rules__condition-row--product'
                        : 'product-rules__condition-row'
                    }
                  >
                    <div className="product-rules__condition-fields">
                    <select
                      className="product-rules__input"
                      style={{ width: 240 }}
                      value={item.field || ''}
                      onChange={(e) => {
                        const nextField = e.target.value;
                        if (!nextField) {
                          updateLeaf(item._uiId, { field: '', op: '', value: {} });
                          return;
                        }
                        const nextOp = (OPERATORS[nextField] || [])[0]?.value || '';
                        const nextValue =
                          nextField === 'collection'
                            ? { collection_ids: [] }
                            : nextField === 'product'
                              ? { product_ids: [] }
                            : nextField === 'product_type'
                              ? { product_types: [] }
                              : nextField === 'status'
                                ? { status: 'ACTIVE' }
                                : nextField === 'created_at'
                                  ? { days: 30 }
                                  : nextField === 'units_sold'
                                    ? { amount: 0, lookback_days: 90 }
                                    : nextField === 'inventory'
                                      ? { amount: 0, location_id: null }
                                      : nextField === 'sales_channel'
                                        ? { publication_id: null }
                                        : {};
                        updateLeaf(item._uiId, { field: nextField, op: nextOp, value: nextValue });
                      }}
                    >
                      <option value="" disabled>
                        Select a condition
                      </option>
                      {FIELD_OPTIONS.filter((o) => o.value !== '').map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>

                    {hasField && (
                      <select
                        className="product-rules__input"
                        style={{ width: 220 }}
                        value={opValue}
                        onChange={(e) => {
                          const nextOp = e.target.value;
                          if (
                            item.field === 'units_sold' &&
                            (nextOp === 'in_top' || nextOp === 'in_bottom')
                          ) {
                            updateLeaf(item._uiId, {
                              op: nextOp,
                              value: { ...item.value, mode: item.value?.mode || 'percent' }
                            });
                          } else {
                            updateLeaf(item._uiId, { op: nextOp });
                          }
                        }}
                      >
                        {fieldOps.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {hasField && renderLeafValueParts({ ...item, op: opValue })}
                    </div>
                    <div className="product-rules__condition-remove">
                      <button
                        type="button"
                        className="product-rules__btn product-rules__btn--secondary"
                        onClick={() => removeItem(index)}
                        aria-label="Remove condition"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  {isProductField && hasField && renderSelectedProductRows({ ...item, op: opValue })}

                  {showOrJoinInsideGroup && (
                    <div className="product-rules__condition-join">
                      <div className="product-rules__condition-join-line" />
                      <select
                        className="product-rules__input"
                        style={{ width: 120 }}
                        value={joins[index] || 'and'}
                        onChange={(e) => updateJoin(index, e.target.value)}
                      >
                        <option value="and">AND</option>
                        <option value="or">OR</option>
                      </select>
                      <div className="product-rules__condition-join-line" />
                    </div>
                  )}
                </div>
              );
                })}

                {/* Add OR condition within this group */}
                <button
                  type="button"
                  className="product-rules__btn product-rules__btn--secondary product-rules__btn--add-condition"
                  onClick={() => {
                    const { items, joins } = getJoinModel();
                    const insertAt = lastIndexInGroup + 1;
                    const nextItems = [
                      ...items.slice(0, insertAt),
                      { _uiId: newUiId(), field: '', op: '', value: {} },
                      ...items.slice(insertAt)
                    ];
                    const nextJoins = [...joins];
                    // Join between last item in this group and the new item should be OR.
                    nextJoins.splice(lastIndexInGroup, 0, 'or');
                    setJoinModel({ items: nextItems, joins: nextJoins });
                  }}
                >
                  + Add condition
                </button>
              </div>

              {/* AND join selector between bordered OR-groups */}
              {!isLastGroup && lastIndexInGroup > -1 && (
                <div className="product-rules__condition-join" style={{ marginTop: 0, marginBottom: 12 }}>
                  <div className="product-rules__condition-join-line" />
                  <select
                    className="product-rules__input"
                    style={{ width: 120 }}
                    value={joins[lastIndexInGroup] || 'and'}
                    onChange={(e) => updateJoin(lastIndexInGroup, e.target.value)}
                  >
                    <option value="and">AND</option>
                    <option value="or">OR</option>
                  </select>
                  <div className="product-rules__condition-join-line" />
                </div>
              )}
            </div>
          );
        })}

      </div>
    );
  };

  const setActionSteps = (steps) => {
    setForm((prev) => ({
      ...prev,
      actions: { ...prev.actions, steps }
    }));
  };

  const renderActionStepFields = (step, uiId) => {
    if (step.type === 'discount') {
      const kind = step.discount_kind === 'fixed' ? 'fixed' : 'percent';
      const value = step.discount_value ?? step.discount_pct ?? '';
      const base = step.discount_base === 'compare_at_price' ? 'compare_at_price' : 'price';
      return (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span className="product-rules__hint" style={{ margin: 0 }}>
                Apply discount
              </span>
              <select
                className="product-rules__input"
                style={{ width: 56 }}
                value={kind}
                onChange={(e) => {
                  const nextKind = e.target.value;
                  setActionSteps(
                    form.actions.steps.map((s) =>
                      s._uiId === uiId
                        ? {
                            ...s,
                            discount_kind: nextKind,
                            discount_value: '',
                            ...(nextKind === 'percent' ? { discount_pct: '' } : {})
                          }
                        : s
                    )
                  );
                }}
              >
                <option value="percent">%</option>
                <option value="fixed">$</option>
              </select>
              <input
                type="number"
                min={kind === 'percent' ? 1 : 0.01}
                max={kind === 'percent' ? 99 : undefined}
                step={kind === 'fixed' ? 0.01 : 1}
                className="product-rules__input"
                style={{ width: 72 }}
                value={value}
                onChange={(e) => {
                  const next = e.target.value;
                  setActionSteps(
                    form.actions.steps.map((s) =>
                      s._uiId === uiId
                        ? {
                            ...s,
                            discount_value: next,
                            ...(kind === 'percent' ? { discount_pct: next } : {})
                          }
                        : s
                    )
                  );
                }}
              />
              <span className="product-rules__hint" style={{ margin: 0 }}>
                off
              </span>
              <select
                className="product-rules__input"
                style={{ minWidth: 160 }}
                value={base}
                onChange={(e) =>
                  setActionSteps(
                    form.actions.steps.map((s) =>
                      s._uiId === uiId ? { ...s, discount_base: e.target.value } : s
                    )
                  )
                }
              >
                <option value="price">current price</option>
                <option value="compare_at_price">compare-at price</option>
              </select>
            </div>
          </div>
          <p className="product-rules__hint" style={{ margin: 0 }}>
          {base === 'compare_at_price'
            ? 'Discount is taken from compare-at when set; otherwise current price is used and saved as compare-at.'
            : 'Current price becomes compare-at when compare-at is not already set.'}
          </p>
        </>
      );
    }
    if (step.type === 'tags') {
      const tagValue = (step.tags || [''])[0] ?? '';
      return (
        <>
          <select
            className="product-rules__input"
            style={{ width: 120 }}
            value={step.op || 'add'}
            onChange={(e) =>
              setActionSteps(
                form.actions.steps.map((s) =>
                  s._uiId === uiId ? { ...s, op: e.target.value } : s
                )
              )
            }
          >
            <option value="add">Add tag</option>
            <option value="remove">Remove tag</option>
          </select>
          <input
            type="text"
            className="product-rules__input"
            style={{ width: 200 }}
            placeholder="Tag name"
            value={tagValue}
            onChange={(e) =>
              setActionSteps(
                form.actions.steps.map((s) =>
                  s._uiId === uiId ? { ...s, tags: [e.target.value] } : s
                )
              )
            }
          />
        </>
      );
    }
    if (step.type === 'status') {
      return (
        <select
          className="product-rules__input"
          style={{ width: 160 }}
          value={step.status || 'ACTIVE'}
          onChange={(e) =>
            setActionSteps(
              form.actions.steps.map((s) =>
                s._uiId === uiId ? { ...s, status: e.target.value } : s
              )
            )
          }
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }
    if (step.type === 'sales_channel') {
      const selectedId = (step.publication_ids || [])[0];
      return (
        <>
          <select
            className="product-rules__input"
            style={{ width: 140 }}
            value={step.op || 'add'}
            onChange={(e) =>
              setActionSteps(
                form.actions.steps.map((s) =>
                  s._uiId === uiId ? { ...s, op: e.target.value } : s
                )
              )
            }
          >
            <option value="add">Add to</option>
            <option value="remove">Remove from</option>
          </select>
          <select
            className="product-rules__input"
            style={{ width: 240 }}
            value={selectedId || ''}
            onChange={(e) =>
              setActionSteps(
                form.actions.steps.map((s) =>
                  s._uiId === uiId
                    ? { ...s, publication_ids: [Number(e.target.value)] }
                    : s
                )
              )
            }
          >
            <option value="">Select sales channel</option>
            {(facets.sales_channels || []).map((sc) => (
              <option key={sc.shopify_publication_id} value={sc.shopify_publication_id}>
                {sc.name}
              </option>
            ))}
          </select>
        </>
      );
    }
    if (step.type === 'collection') {
      const selectedId = (step.collection_ids || [])[0];
      return (
        <>
          <select
            className="product-rules__input"
            style={{ width: 140 }}
            value={step.op || 'add'}
            onChange={(e) =>
              setActionSteps(
                form.actions.steps.map((s) =>
                  s._uiId === uiId ? { ...s, op: e.target.value } : s
                )
              )
            }
          >
            <option value="add">Add to</option>
            <option value="remove">Remove from</option>
          </select>
          <select
            className="product-rules__input"
            style={{ width: 220 }}
            value={selectedId != null ? String(selectedId) : ''}
            onChange={(e) => {
              const id = Number(e.target.value);
              setActionSteps(
                form.actions.steps.map((s) =>
                  s._uiId === uiId
                    ? {
                        ...s,
                        collection_ids: Number.isFinite(id) && id > 0 ? [id] : []
                      }
                    : s
                )
              );
            }}
          >
            <option value="" disabled>
              Select collection
            </option>
            {facets.collections.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name || `Collection ${c.id}`}
              </option>
            ))}
          </select>
        </>
      );
    }
    return null;
  };

  const renderActionsBuilder = () => {
    const steps = Array.isArray(form.actions?.steps) ? form.actions.steps : [];
    return (
      <div>
        {steps.map((step) => (
          <div key={step._uiId} className="product-rules__condition-row" style={{ marginBottom: 10 }}>
            <div className="product-rules__condition-fields">
              <select
                className="product-rules__input"
                style={{ width: 200 }}
                value={step.type || ''}
                onChange={(e) => {
                  const nextType = e.target.value;
                  if (!nextType) {
                    setActionSteps(steps.filter((s) => s._uiId !== step._uiId));
                    return;
                  }
                  setActionSteps(
                    steps.map((s) =>
                      s._uiId === step._uiId
                        ? { _uiId: s._uiId, ...defaultActionStep(nextType) }
                        : s
                    )
                  );
                }}
              >
                {ACTION_TYPE_OPTIONS.map((o) => (
                  <option key={o.value || 'empty'} value={o.value} disabled={o.value === ''}>
                    {o.label}
                  </option>
                ))}
              </select>
              {step.type ? renderActionStepFields(step, step._uiId) : null}
            </div>
            <div className="product-rules__condition-remove">
              <button
                type="button"
                className="product-rules__btn product-rules__btn--secondary"
                onClick={() => setActionSteps(steps.filter((s) => s._uiId !== step._uiId))}
                aria-label="Remove action"
              >
                ×
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="product-rules__btn product-rules__btn--secondary product-rules__btn--add-condition"
          onClick={() => setActionSteps([...steps, { _uiId: newUiId(), type: '' }])}
        >
          + Add action
        </button>
        <label className="product-rules__checkbox product-rules__checkbox--block" style={{ marginTop: 16 }}>
          <input
            type="checkbox"
            checked={Boolean(form.actions?.revert_previous)}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                actions: { ...prev.actions, revert_previous: e.target.checked }
              }))
            }
          />
          Revert products from the previous run before applying
        </label>
        <p className="product-rules__hint">
          Uses Amplify&apos;s record of products this rule last changed. Works even when the rule
          does not use tags.
        </p>
      </div>
    );
  };

  const isTreeComplete = useMemo(() => {
    const apiTree = buildConditionsTreeForApi(form.conditions_tree);
    const hasComplete = (apiTree.children || []).some((g) => (g.children || []).length > 0);
    return hasComplete && !hasIncompleteStartedLeaf(form.conditions_tree);
  }, [form.conditions_tree]);

  const isActionsComplete = useMemo(() => {
    const steps = form.actions?.steps || [];
    const complete = steps.filter(isActionStepComplete);
    return complete.length > 0 && steps.every((s) => !s.type || isActionStepComplete(s));
  }, [form.actions]);

  const previewPayload = useMemo(() => {
    if (!isTreeComplete) return null;
    return formToPreviewPayload(form);
  }, [form, isTreeComplete]);

  useEffect(() => {
    if (!editorOpen || !previewPayload) {
      setEligibilityCount(null);
      setEligibilityLoading(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setEligibilityLoading(true);
      try {
        const res = await req({
          method: 'POST',
          url: `${API}/app/product-analysis/rules/preview`,
          data: { ...previewPayload, include_products: false }
        });
        if (!cancelled) setEligibilityCount(res.data?.count ?? 0);
      } catch {
        if (!cancelled) setEligibilityCount(null);
      } finally {
        if (!cancelled) setEligibilityLoading(false);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [editorOpen, previewPayload, req]);

  const openProductPreview = async (page = 0) => {
    if (!previewPayload) return;
    if (!previewOpen) {
      setPreviewOpen(true);
      setPreviewPage(0);
      page = 0;
    }
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const res = await req({
        method: 'POST',
        url: `${API}/app/product-analysis/rules/preview`,
        data: { 
          ...previewPayload, 
          include_products: true,
          product_limit: previewLimit,
          offset: page * previewLimit
        }
      });
      setEligibilityCount(res.data?.count ?? 0);
      setPreviewTotal(res.data?.count ?? 0);
      setPreviewProducts(res.data?.products || []);
      setPreviewPage(page);
    } catch (e) {
      setPreviewError(e.response?.data?.message || e.message || 'Preview failed.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const nextPreviewPage = () => {
    const totalPages = Math.ceil(previewTotal / previewLimit);
    if (previewPage + 1 < totalPages) {
      openProductPreview(previewPage + 1);
    }
  };

  const prevPreviewPage = () => {
    if (previewPage > 0) {
      openProductPreview(previewPage - 1);
    }
  };

  if (loading) {
    return (
      <div className="product-analysis-scroll">
        <Loading />
      </div>
    );
  }

  return (
    <div className="product-analysis-scroll">
    <div className="product-rules">
      <header className="product-rules__header">
        <div>
          <h1 className="product-rules__title">Product Rules</h1>
          <p className="product-rules__subtitle">
            Schedule catalog automation — match slow movers and apply pricing or tags on a daily,
            weekly, or monthly cadence.
          </p>
        </div>
        {canWrite && (
          <div className="product-rules__header-actions">
            <button type="button" className="product-rules__btn" onClick={openCreate}>
              New rule
            </button>
          </div>
        )}
      </header>

      {!writeScope && (
        <div className="product-rules__banner product-rules__banner--warn" role="alert">
          Shopify <strong>write_products</strong> scope is missing. Re-authorize your Shopify
          integration before rules can update prices or tags.
        </div>
      )}

      {!publicationsScope && (
        <div className="product-rules__banner product-rules__banner--warn" role="alert">
          Shopify <strong>write_publications</strong> scope is missing. Re-authorize your Shopify
          integration before rules can add or remove products from sales channels.
        </div>
      )}

      {err && (
        <div className="product-rules__banner product-rules__banner--error" role="alert">
          {err}
        </div>
      )}

      {rules.length === 0 && !editorOpen ? (
        <div className="product-rules__empty">
          <p>No rules yet.</p>
          {canWrite && (
            <button type="button" className="product-rules__btn" onClick={openCreate}>
              Create your first rule
            </button>
          )}
        </div>
      ) : (
        <div className="product-rules__list">
          {rules.map((rule) => (
            <article key={rule.id} className="product-rules__card">
              <div className="product-rules__card-main">
                <div className="product-rules__card-head">
                  <h2 className="product-rules__card-title">{rule.name}</h2>
                  <div className="product-rules__toggle">
                    <label className="switch">
                      <input
                        className="switch-input"
                        type="checkbox"
                        checked={rule.enabled}
                        value={rule.enabled ? 'true' : 'false'}
                        disabled={!canWrite || toggleBusyId === rule.id}
                        onChange={() => toggleEnabled(rule)}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                </div>
                <p className="product-rules__schedule">{rule.schedule_summary}</p>
                <p className="product-rules__meta">Last run: {formatLastRun(rule, timezone)}</p>
              </div>
              <div className="product-rules__card-actions">
                <button
                  type="button"
                  className="product-rules__btn product-rules__btn--secondary"
                  onClick={() =>
                    setHistoryRuleId(historyRuleId === rule.id ? null : rule.id)
                  }
                >
                  {historyRuleId === rule.id ? 'Hide history' : 'History'}
                </button>
                {canWrite && (
                  <>
                    <button
                      type="button"
                      className="product-rules__btn product-rules__btn--secondary"
                      onClick={() => openEdit(rule)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="product-rules__btn"
                      disabled={
                        !writeScope ||
                        (!publicationsScope && ruleUsesSalesChannelActions(rule)) ||
                        ruleRunBlocked(rule, runBusyId)
                      }
                      onClick={() => runNow(rule)}
                    >
                      {runNowButtonLabel(rule, runBusyId)}
                    </button>
                    <button
                      type="button"
                      className="product-rules__btn product-rules__btn--danger"
                      onClick={() => deleteRule(rule)}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
              {historyRuleId === rule.id && (
                <div className="product-rules__history">
                  {runsLoading ? (
                    <p>Loading runs…</p>
                  ) : runs.length === 0 && runBusyId === rule.id ? (
                    <p className="product-rules__hint">Run queued — waiting for results…</p>
                  ) : runs.length === 0 ? (
                    <p>No runs recorded yet.</p>
                  ) : (
                    <table className="product-rules__history-table">
                      <thead>
                        <tr>
                          <th>Started</th>
                          <th>Status</th>
                          <th>Reverted</th>
                          <th>Matched</th>
                          <th>Applied</th>
                          <th>Failed</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {runs.map((run) => (
                          <Fragment key={run.id}>
                            <tr>
                              <td>{formatStoreDateTime(run.started_at, timezone)}</td>
                              <td>{run.status}</td>
                              <td>{run.reverted_count ?? run.cleared_count ?? 0}</td>
                              <td>{run.matched_count ?? 0}</td>
                              <td>{run.applied_count ?? 0}</td>
                              <td>{run.failed_count ?? 0}</td>
                              <td>
                                <button
                                  type="button"
                                  className="product-rules__btn product-rules__btn--secondary"
                                  style={{ padding: '4px 8px', fontSize: '0.8rem' }}
                                  onClick={() => loadRunDetail(rule.id, run.id)}
                                >
                                  {expandedRunId === run.id ? 'Hide' : 'View'} products
                                </button>
                              </td>
                            </tr>
                            {expandedRunId === run.id && (
                              <tr key={`${run.id}-detail`}>
                                <td colSpan={7}>
                                  {runDetailLoading ? (
                                    <p className="product-rules__hint">Loading…</p>
                                  ) : runDetail?.products?.length ? (
                                    <ul className="product-rules__run-products">
                                      {runDetail.products.map((p) => (
                                        <li key={p.shopify_product_id}>
                                          <span className="product-rules__run-product-title">
                                            {p.title || `Product ${p.shopify_product_id}`}
                                          </span>
                                          <span
                                            className={
                                              p.status === 'failed'
                                                ? 'product-rules__run-product-status--failed'
                                                : 'product-rules__run-product-status--ok'
                                            }
                                          >
                                            {p.status}
                                          </span>
                                          {p.steps_applied?.length ? (
                                            <span className="product-rules__hint">
                                              {p.steps_applied.join(', ')}
                                            </span>
                                          ) : null}
                                          {p.error ? (
                                            <span className="product-rules__err">{p.error}</span>
                                          ) : null}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="product-rules__hint">No product details recorded.</p>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {editorOpen && (
        <div className="product-rules__modal-backdrop" onClick={closeEditor} role="presentation">
          <div
            className="product-rules__modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="product-rules-editor-title"
          >
            <h2 id="product-rules-editor-title" className="product-rules__modal-title">
              {editingId ? 'Edit rule' : 'New rule'}
            </h2>

            <section className="product-rules__section">
              <label className="product-rules__label">
                Name
                <input
                  type="text"
                  className="product-rules__input"
                  value={form.name}
                  onChange={(e) => updateForm('name', e.target.value)}
                />
              </label>
            </section>

            <section className="product-rules__section">
              <h3 className="product-rules__section-title">Schedule</h3>
              <p className="product-rules__hint">Store timezone: {timezone || DEFAULT_STORE_TIMEZONE}</p>
              <div className="product-rules__row">
                <label className="product-rules__label">
                  Frequency
                  <select
                    className="product-rules__input"
                    value={form.schedule.frequency}
                    onChange={(e) => updateForm('schedule.frequency', e.target.value)}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </label>
                <label className="product-rules__label">
                  Time (local)
                  <input
                    type="time"
                    className="product-rules__input"
                    value={form.schedule.time_local}
                    onChange={(e) => updateForm('schedule.time_local', e.target.value)}
                  />
                </label>
              </div>
              {form.schedule.frequency === 'weekly' && (
                <label className="product-rules__label">
                  Day of week
                  <select
                    className="product-rules__input"
                    value={form.schedule.day_of_week}
                    onChange={(e) =>
                      updateForm('schedule.day_of_week', Number(e.target.value))
                    }
                  >
                    {WEEKDAYS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {form.schedule.frequency === 'monthly' && (
                <label className="product-rules__label">
                  Day of month (1–28)
                  <input
                    type="number"
                    min={1}
                    max={28}
                    className="product-rules__input"
                    value={form.schedule.day_of_month}
                    onChange={(e) =>
                      updateForm('schedule.day_of_month', Number(e.target.value))
                    }
                  />
                </label>
              )}
            </section>

            <section className="product-rules__section">
              <div className="product-rules__section-header">
                <div className="product-rules__section-heading">
                  <h3 className="product-rules__section-title">Product eligibility</h3>
                  <ToolTip
                    className="product-rules__eligibility-tip"
                    message="Conditions joined with AND are applied in the order shown, top to bottom. Each step narrows the product set before the next runs."
                    width={280}
                  />
                </div>
                <div className="product-rules__eligibility-stat" aria-live="polite">
                  <i className="fa-solid fa-box product-rules__eligibility-icon" aria-hidden />
                  <span className="product-rules__eligibility-count">
                    {eligibilityLoading
                      ? '…'
                      : previewPayload
                        ? eligibilityCount != null
                          ? eligibilityCount.toLocaleString()
                          : '—'
                        : '—'}
                  </span>
                </div>
              </div>
              {renderAndOrBuilder()}
              <div className="product-rules__row" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="product-rules__btn product-rules__btn--secondary"
                  disabled={!previewPayload || previewLoading}
                  onClick={openProductPreview}
                >
                  See Preview
                </button>
              </div>
              <label className="product-rules__checkbox product-rules__checkbox--block" style={{ marginTop: 16 }}>
                <input
                  type="checkbox"
                  checked={form.selection != null}
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateForm('selection', { count: 50, method: 'random' });
                    } else {
                      updateForm('selection', null);
                    }
                  }}
                />
                Hard cap number of products each run
              </label>
              {form.selection != null && (
                <>
                  <label className="product-rules__label" style={{ marginTop: 12 }}>
                    Max products per run
                    <input
                      type="number"
                      min={1}
                      max={500}
                      className="product-rules__input"
                      value={form.selection.count ?? 50}
                      onChange={(e) => updateForm('selection.count', e.target.value)}
                    />
                  </label>
                  <p className="product-rules__hint">
                    If there are more eligible products than this number, a random subset is chosen
                    each time the rule runs.
                  </p>
                </>
              )}
            </section>

            <section className="product-rules__section">
              <h3 className="product-rules__section-title">Actions</h3>
              <p className="product-rules__hint" style={{ marginTop: 0 }}>
                All actions run on each selected product, in order from top to bottom.
              </p>
              {renderActionsBuilder()}
            </section>

            <div className="product-rules__modal-actions">
              <button
                type="button"
                className="product-rules__btn product-rules__btn--secondary"
                onClick={closeEditor}
              >
                Cancel
              </button>
              <button
                type="button"
                className="product-rules__btn"
                disabled={saving || !form.name.trim() || !isTreeComplete || !isActionsComplete}
                onClick={saveRule}
              >
                {saving ? 'Saving…' : 'Save rule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewOpen && (
        <div
          className="product-rules__modal-backdrop product-rules__modal-backdrop--stacked"
          role="presentation"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="product-rules__preview-panel"
            role="dialog"
            aria-labelledby="product-rules-preview-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="product-rules__preview-header">
              <h4 id="product-rules-preview-title" className="product-rules__preview-title">
                Product preview
              </h4>
              <button
                type="button"
                className="product-rules__preview-close"
                onClick={() => setPreviewOpen(false)}
                aria-label="Close preview"
              >
                ×
              </button>
            </div>
            {previewLoading && (
              <div className="product-rules__preview-loading">
                <Loading />
              </div>
            )}
            {previewError && <p className="product-rules__err" style={{ padding: '0 1.35rem' }}>{previewError}</p>}
            {!previewLoading && !previewError && (
              <>
                <p className="product-rules__hint product-rules__preview-summary">
                  {previewTotal > 0 ? (
                    <>
                      {previewTotal.toLocaleString()} product{previewTotal === 1 ? '' : 's'} match your conditions.
                      {' '}Showing {(previewPage * previewLimit + 1).toLocaleString()}–
                      {Math.min((previewPage + 1) * previewLimit, previewTotal).toLocaleString()} of{' '}
                      {previewTotal.toLocaleString()}.
                    </>
                  ) : (
                    'No products match these conditions yet.'
                  )}
                </p>
                <div className="product-rules__preview-scroll">
                  {previewProducts.length > 0 ? (
                    <ul className="product-rules__preview-grid">
                      {previewProducts.map((p) => (
                        <li key={p.shopify_product_id} className="product-rules__preview-card">
                          {p.image_url ? (
                            <img
                              className="product-rules__preview-img"
                              src={p.image_url}
                              alt=""
                            />
                          ) : (
                            <div className="product-rules__preview-img product-rules__preview-img--placeholder">
                              <ImagePlaceholderIcon className="product-rules__preview-placeholder-icon" />
                            </div>
                          )}
                          <span className="product-rules__preview-name">{p.title}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </>
            )}
            {previewTotal > previewLimit && !previewLoading && !previewError && (
              <div className="product-rules__preview-footer">
                <div className="product-rules__preview-pagination">
                  <button
                    type="button"
                    className="product-rules__btn product-rules__btn--secondary"
                    disabled={previewPage === 0 || previewLoading}
                    onClick={prevPreviewPage}
                  >
                    ← Previous
                  </button>
                  <span className="product-rules__preview-page-info">
                    Page {previewPage + 1} of {Math.ceil(previewTotal / previewLimit)}
                  </span>
                  <button
                    type="button"
                    className="product-rules__btn product-rules__btn--secondary"
                    disabled={
                      (previewPage + 1) * previewLimit >= previewTotal || previewLoading
                    }
                    onClick={nextPreviewPage}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
